import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/line.css';

import { db } from '../../firebase/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import useLineAuth from '../hooks/useLineAuth';
import { cancelReservation } from '../../firebase/callFunctions';

export default function ReservedLine() {
  const navigate = useNavigate();
  const { profile } = useLineAuth(); // ようこそ表記用（任意）
  const [resv, setResv] = useState(null);   // { no, slotId, by, reservedType, at }
  const [waitCount, setWaitCount] = useState(null);
  const [busyCancel, setBusyCancel] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  // 予約情報をローカルから読み込み
  useEffect(() => {
    const raw = localStorage.getItem('latestReservationLine');
    const data = raw ? safeParse(raw) : null;
    setResv(data);
    // 無ければ予約画面へ
    if (!data) navigate('/line/book', { replace: true });
  }, [navigate]);

  // 現在の待ち人数を購読
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'admin', 'runtime'), (snap) => {
      const d = snap.data() || {};
      setWaitCount(Number.isFinite(d.currentWaitCount) ? d.currentWaitCount : null);
    });
    return () => unsub();
  }, []);

  const reservedAtText = useMemo(() => {
    if (!resv?.at) return "—";
    const d = new Date(resv.at);
    const pad = (n) => `${n}`.padStart(2, '0');
    const w = '日月火水木金土'[d.getDay()];
    return `${d.getFullYear()}/${pad(d.getMonth()+1)}/${pad(d.getDate())}(${w}) ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, [resv]);

  const handleCancel = async () => {
    if (!resv) return;
    if (busyCancel) return;
    setErrMsg("");
    setBusyCancel(true);
    try {
      await cancelReservation({
        slotId: resv.slotId,
        reservationNumber: resv.no,
        canceledBy: "LINE",
      });
      // ローカルをクリアして受付トップへ戻す
      localStorage.removeItem('latestReservationLine');
      navigate('/line/book', { replace: true });
    } catch (e) {
      console.error(e);
      const code = e?.code;
      let msg = e?.message || "予約の取消に失敗しました。時間をおいて再度お試しください。";
      if (code === "not-found") msg = "予約が見つかりませんでした。";
      setErrMsg(msg);
      alert(msg); // スマホ検証用（不要なら削除）
    } finally {
      setBusyCancel(false);
    }
  };

  if (!resv) {
    return null; // useEffectで /line/book に飛ばす
  }

  return (
    <div className="line-container">
      <div className="line-card">

        {/* ヘッダ */}
        <div className="line-wait" style={{ marginBottom: 12 }}>
          <div className="line-wait-caption">あなたの受付番号</div>
          <div className="line-number-value">{resv.no ?? '—'}</div>
          <div className="line-muted">{reservedAtText}</div>
        </div>

        {/* 現在の待ち人数 */}
        <div className="line-wait" style={{ marginTop: 6, marginBottom: 14 }}>
          <div className="line-wait-caption">現在の待ち人数</div>
          <div className="line-wait-count">{waitCount === null ? '—' : `${waitCount}人`}</div>
        </div>

        {/* 説明 */}
        <p className="line-desc" style={{ marginTop: 4 }}>
          ご予約ありがとうございます。<br />
          <b>来院時にこの画面をスタッフまでご提示</b>ください。
        </p>

        {/* 取消ボタン */}
        <button
          className="line-danger-btn"
          onClick={handleCancel}
          disabled={busyCancel}
        >
          {busyCancel ? "取消しています…" : "予約を取消する"}
        </button>

        {/* エラー */}
        {errMsg && <p className="line-error" role="alert" aria-live="assertive">{errMsg}</p>}

        {/* ようこそ */}
        {profile?.displayName && (
          <div className="line-welcome" style={{ marginTop: 12 }}>
            ようこそ、{profile.displayName} さん
          </div>
        )}
      </div>
    </div>
  );
}

function safeParse(s) {
  try { return JSON.parse(s); } catch { return null; }
}
